import React, { useEffect, useState } from 'react'

import api from 'src/api'
import { PenaltyScheme, PolicyAmount } from 'src/api/gen/api'
import { JoyrideWrapper } from 'src/components/Joyride'
import {
  IPenaltySchemeStepIdEnum,
  PenaltySchemeSteps,
} from 'src/Joyride/HelpingJoyride/steps/policies/penaltyScheme.steps'
import Loader from 'src/layout/Loader'

import PenaltySchemeEditor from './Editor'
import { PenaltySchemeList } from './PenaltySchemeList'

export type IPenaltyScheme = Omit<PenaltyScheme, 'policies_amounts'>

function PenaltySchemesPage() {
  const [penaltyScheme, setPenaltyScheme] = useState<IPenaltyScheme | null>()
  const [policyAmounts, setPolicyAmounts] = useState<Map<string, PolicyAmount[]>>(new Map())

  const fetchAndSetSchemeAndPolicies = async function (signal: AbortSignal) {
    try {
      const { policies_amounts, ...scheme } = await api.penaltySchemes.penaltySchemesList({
        is_fee_rates: false,
        options: { signal },
      })
      // We still want to display data for providers with no policies
      const noPoliciesProviders = scheme.providers
        .filter((provider) => !(provider in policies_amounts))
        .map<[string, any[]]>((provider) => [provider, []]) // manual typing is required because never is not compatible
      setPenaltyScheme({ ...scheme })
      setPolicyAmounts(new Map([...Object.entries(policies_amounts), ...noPoliciesProviders]))
    } catch (exception: any) {
      if (exception.status == 404) {
        setPenaltyScheme(null)
      }
    }
  }

  async function archiveScheme() {
    if (penaltyScheme) {
      await api.penaltySchemes.penaltySchemesDelete({ id: penaltyScheme.id })
      setPenaltyScheme(null)
    }
  }

  useEffect(() => {
    const abortController = new AbortController()
    fetchAndSetSchemeAndPolicies(abortController.signal)
    return () => abortController.abort()
  }, [])

  if (penaltyScheme === undefined) {
    return <Loader />
  }

  if (penaltyScheme === null) {
    return (
      <JoyrideWrapper
        stepsTranslator={PenaltySchemeSteps}
        steps={[
          IPenaltySchemeStepIdEnum.penaltyScheme_general,
          IPenaltySchemeStepIdEnum.penaltyScheme_fields,
          IPenaltySchemeStepIdEnum.penaltyScheme_actions,
        ]}
      >
        <PenaltySchemeEditor setPenaltyScheme={setPenaltyScheme} setPolicyAmounts={setPolicyAmounts} />
      </JoyrideWrapper>
    )
  }

  return <PenaltySchemeList penaltyScheme={penaltyScheme} policyAmounts={policyAmounts} archiveScheme={archiveScheme} />
}

export default PenaltySchemesPage
